import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaShare } from 'react-icons/fa';
import { isAndroid, isIOS } from '../utils/device';
import { Button } from './Button';
import { Modal } from './Modal';

interface InstallPwaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const InstallPwaModal: React.FC<InstallPwaModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation(['pwa', 'common']);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(() => {
        setDeferredPrompt(null);
        onClose();
      });
    }
  };

  const renderIOsInstructions = () => (
    <div className="space-y-4">
      <div>
        <p className="font-semibold">{t('pwa:ios.step1.title')}</p>
        <p className="text-sm text-gray-600">
          {t('pwa:ios.step1.description')}
        </p>
      </div>
      <div>
        <p className="font-semibold">{t('pwa:ios.step2.title')}</p>
        <p className="text-sm text-gray-600">
          {t('pwa:ios.step2.description')}
          <FaShare className="mx-1 inline" />
        </p>
      </div>
      <div>
        <p className="font-semibold">{t('pwa:ios.step3.title')}</p>
        <p className="text-sm text-gray-600">
          {t('pwa:ios.step3.description')}
        </p>
      </div>
    </div>
  );

  const renderAndroidInstructions = () => (
    <div className="space-y-4">
      <div>
        <p className="font-semibold">{t('pwa:android.step1.title')}</p>
        <p className="text-sm text-gray-600">
          {t('pwa:android.step1.description')}
        </p>
      </div>
      <div>
        <p className="font-semibold">{t('pwa:android.step2.title')}</p>
        <p className="text-sm text-gray-600">
          {t('pwa:android.step2.description')}
        </p>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pwa:title')}
      className="max-w-lg"
    >
      <div className="text-gray-600">{t('pwa:description')}</div>

      <div className="mt-4">
        <div className="mt-4 max-h-80 overflow-y-auto">
          {isIOS() && renderIOsInstructions()}
          {isAndroid() && renderAndroidInstructions()}
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        {isAndroid() && deferredPrompt && (
          <Button
            onClick={handleInstallClick}
            className="flex items-center gap-2"
          >
            <FaDownload /> {t('pwa:installButton')}
          </Button>
        )}
      </div>
    </Modal>
  );
};
